import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import type { CSSProperties, ReactNode } from 'react';

import './index.css';

type StyleCustom = {
  modal?: CSSProperties;
  btnCancel?: CSSProperties;
  btnAccept?: CSSProperties;
};

interface Props {
  show: boolean;
  acceptSubmit: () => void;
  cancelSubmit: () => void;
  message: string | ReactNode;
  btnAcceptText: string;
  btnCancelText?: string;
  styleCustom?: StyleCustom;
  disabled?: boolean;
  modalId?: string;
  startIcon?: ReactNode;
}

const style = {
  modalContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    height: 'auto',
    width: 'auto',
    '@media (max-width: 600px)': {
      width: '565px'
    },
    bgcolor: '#FFFFFF',
    borderRadius: '6px'
  },
  content: {
    display: 'flex',
    position: 'relative' as const,
    height: '100%',
    flexDirection: 'column' as const,
    padding: '6px 36px 36px 36px'
  },
  titleContainer: {
    display: 'flex',
    flex: '1',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '10px'
  },
  typographyMessage: {
    whiteSpace: 'pre-wrap',
    color: '#063468',
    textAlign: 'center',
    fontWeight: '400',
    wordBreak: 'break-all',
    maxHeight: '500px',
    overflowY: 'auto'
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-around'
  },
  button: {
    width: '100%',
    maxWidth: '150px',
    margin: '10px 5px 0px 5px'
  }
};

function CustomBasicOptionsModal({
  show,
  acceptSubmit,
  cancelSubmit,
  message,
  btnAcceptText,
  btnCancelText,
  styleCustom,
  disabled = false,
  modalId = '',
  startIcon = null
}: Props) {
  return (
    <Modal
      className="CustomBasicOptionsModalResponsive"
      id={modalId}
      onClose={cancelSubmit}
      open={show}
      style={styleCustom?.modal || {}}
    >
      <Box sx={style.modalContainer}>
        <div
          style={{
            height: '30px'
          }}
        >
          <IconButton
            aria-label="close"
            onClick={cancelSubmit}
            sx={{
              position: 'absolute',
              right: 0,
              top: '-3px',
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div style={style.content}>
          <div style={style.titleContainer}>
            {!message && <CircularProgress color="primary" size={30} />}
            <Typography sx={style.typographyMessage}>{message}</Typography>
          </div>
          <div style={style.buttonContainer}>
            {btnCancelText && (
              <Button
                disabled={disabled}
                onClick={cancelSubmit}
                size="medium"
                style={style.button}
                sx={styleCustom?.btnCancel}
                type="submit"
                variant="outlined"
              >
                {btnCancelText}
              </Button>
            )}

            <Button
              disabled={disabled}
              onClick={acceptSubmit}
              size="medium"
              startIcon={startIcon}
              style={style.button}
              sx={styleCustom?.btnAccept}
              type="submit"
              variant="contained"
            >
              {btnAcceptText}
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default CustomBasicOptionsModal;
